/**
 * サブスクリプション復活プレビューダイアログ
 * 復活時の料金見積もりとアドオン選択を表示
 * @module components/subscription/ReactivationPreviewDialog
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Space,
  Alert,
  Checkbox,
  Radio,
  Divider,
  Spin,
  Descriptions,
  Card,
} from 'antd';
import {
  ReloadOutlined,
  DollarOutlined,
  AppstoreAddOutlined,
} from '@ant-design/icons';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { trpc } from '../../lib/trpc';
import type { Company } from '../../types/company';
import { withDevModalFilePath } from '../common';
import {
  SUBSCRIPTION_PLAN_CONFIGS,
  PUBLIC_PLAN_ORDER,
} from '../../../../shared/config/subscription-plans';
import { ADDON_CONFIGS } from '../../../../shared/config/subscription-addons';

const { Text, Title } = Typography;
const { Modal } = withDevModalFilePath('client/src/components/subscription/ReactivationPreviewDialog.tsx');

interface ReactivationPreviewDialogProps {
  open: boolean;
  company: Company;
  onConfirm: (planCode: string, addOnIndices: number[]) => void;
  onCancel: () => void;
  loading?: boolean;
}

/**
 * 日付フォーマット
 */
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return '-';
  return format(new Date(date), 'yyyy年MM月dd日', { locale: ja });
};

/**
 * 金額フォーマット
 */
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * サブスクリプション復活プレビューダイアログ
 */
export function ReactivationPreviewDialog({
  open,
  company,
  onConfirm,
  onCancel,
  loading = false,
}: ReactivationPreviewDialogProps) {
  // 選択状態
  const [selectedPlanCode, setSelectedPlanCode] = useState<string>(
    company.subscriptionPlan || 'standard'
  );
  const [selectedAddOnIndices, setSelectedAddOnIndices] = useState<number[]>([]);

  // プランが変更されたらリセット
  useEffect(() => {
    if (open) {
      setSelectedPlanCode(company.subscriptionPlan || 'standard');
      setSelectedAddOnIndices([]);
    }
  }, [open, company.subscriptionPlan]);

  // プレビューデータを取得
  const { data: preview, isLoading: isPreviewLoading } =
    trpc.subscriptionCancellation.previewReactivation.useQuery(
      {
        companyId: company.id,
        planCode: selectedPlanCode,
        addOnIndices: selectedAddOnIndices,
      },
      {
        enabled: open && !!selectedPlanCode,
        staleTime: 10000, // 10秒キャッシュ
      }
    );

  // アドオンチェックボックスの変更ハンドラ
  const handleAddOnChange = (index: number, checked: boolean) => {
    if (checked) {
      setSelectedAddOnIndices([...selectedAddOnIndices, index]);
    } else {
      setSelectedAddOnIndices(selectedAddOnIndices.filter(i => i !== index));
    }
  };

  // 利用可能なプラン一覧
  const availablePlans = useMemo(() => {
    return PUBLIC_PLAN_ORDER.map(code => ({
      code,
      ...SUBSCRIPTION_PLAN_CONFIGS[code],
    }));
  }, []);

  // 確認ハンドラ
  const handleConfirm = () => {
    onConfirm(selectedPlanCode, selectedAddOnIndices);
  };

  return (
    <Modal
      title={
        <Space>
          <ReloadOutlined style={{ color: '#52c41a', fontSize: 20 }} />
          <Title level={4} style={{ margin: 0 }}>
            サブスクリプションを復活する
          </Title>
        </Space>
      }
      open={open}
      onOk={handleConfirm}
      onCancel={onCancel}
      okText="復活を確定する"
      okType="primary"
      cancelText="キャンセル"
      okButtonProps={{ loading, disabled: isPreviewLoading }}
      width={640}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 16 }}>
        {/* プラン選択 */}
        <Card
          size="small"
          title={
            <Space>
              <DollarOutlined />
              プラン選択
            </Space>
          }
        >
          <Radio.Group
            value={selectedPlanCode}
            onChange={e => setSelectedPlanCode(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {availablePlans.map(plan => (
                <Radio key={plan.code} value={plan.code} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: 480 }}>
                    <span>{plan.label}</span>
                    <Text type="secondary">
                      {formatCurrency(plan.monthlyPrice)}/月
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                        (税込 {formatCurrency(Math.floor(plan.monthlyPrice * 1.1))})
                      </Text>
                    </Text>
                  </div>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Card>

        {/* 以前のアドオン復活 */}
        {preview && preview.previousAddOns.length > 0 && (
          <Card
            size="small"
            title={
              <Space>
                <AppstoreAddOutlined />
                以前の追加オプションを復活
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {preview.previousAddOns.map(addon => (
                <Checkbox
                  key={addon.index}
                  checked={selectedAddOnIndices.includes(addon.index)}
                  onChange={e => handleAddOnChange(addon.index, e.target.checked)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: 460 }}>
                    <span>
                      {ADDON_CONFIGS[addon.type]?.label || addon.type} +{addon.quantity}
                      {ADDON_CONFIGS[addon.type]?.unitLabel || ''}
                    </span>
                    <Text type="secondary">
                      {formatCurrency(addon.totalPrice)}/月
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                        (税込 {formatCurrency(addon.totalPriceWithTax)})
                      </Text>
                    </Text>
                  </div>
                </Checkbox>
              ))}
              {preview.previousAddOns.length === 0 && (
                <Text type="secondary">以前の追加オプションはありません</Text>
              )}
            </Space>
          </Card>
        )}

        <Divider style={{ margin: '8px 0' }} />

        {/* 料金プレビュー */}
        {isPreviewLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">料金を計算中...</Text>
            </div>
          </div>
        ) : preview ? (
          <Card
            size="small"
            title="お支払い概算"
            style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="基本プラン（日割り）">
                <Text strong>
                  {formatCurrency(preview.proration.proratedAmountWithTax)}
                </Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  ({preview.proration.daysRemaining}日分)
                </Text>
              </Descriptions.Item>

              {selectedAddOnIndices.length > 0 && (
                <Descriptions.Item label="追加オプション（日割り）">
                  <Text strong>
                    {formatCurrency(preview.selectedAddOns.proratedAmountWithTax)}
                  </Text>
                </Descriptions.Item>
              )}

              <Descriptions.Item label="初回請求額（税込）">
                <Text strong style={{ fontSize: 18, color: '#52c41a' }}>
                  {formatCurrency(preview.totals.initialChargeWithTax)}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: '12px 0' }} />

            <Descriptions column={1} size="small">
              <Descriptions.Item label="次回以降の月額（税込）">
                <Text strong>
                  {formatCurrency(preview.totals.monthlyRecurringWithTax)}/月
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="請求期間">
                {formatDate(preview.proration.periodStart)} 〜 {formatDate(preview.proration.periodEnd)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        ) : null}

        {/* 注意事項 */}
        <Alert
          type="info"
          showIcon
          message="注意事項"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>復活後、サブスクリプションは即座にアクティブになります</li>
              <li>初回請求は復活日から月末までの日割り計算となります</li>
              <li>翌月以降は毎月末に定期請求されます</li>
              <li>この操作は監査ログに記録されます</li>
            </ul>
          }
        />
      </Space>
    </Modal>
  );
}
